import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { selectUser, getSubscription } from '../../../reducers/user.selectors';
import PricingPage from './PricingPage';
import NewPricingPage from './NewPricingPage';
import { newPricingPackageLicenceIds, userType, routes } from '../../../constants';
import useRouter from '../../../utils/useRouter';

const PricingPageWrapper: React.FC = () => {
  const user = useSelector(selectUser);
  const subscription = useSelector(getSubscription);
  const { router } = useRouter();

  const currentPlanUsesNewPricing = subscription?.licence?.licenceModelId &&
      Object.values(newPricingPackageLicenceIds).includes(Number(subscription?.licence?.licenceModelId));

  // SM-392 scopes this to Zuora Basis users only — Stripe Basis users are
  // explicitly out of scope and keep falling through to the pricing pages below.
  const isZuoraBasisUser = user?.type === userType.basis && !user?.isStripeUser;

  // SM-392: Zuora Basis users must never land on the legacy Zuora PricingPage —
  // send them straight to the Stripe checkout deep link the ticket specifies
  // instead. This is a single choke point for every internal path that lands
  // a Basis user on /premium, so nothing renders here for them while it navigates away.
  useEffect(() => {
    if (isZuoraBasisUser) {
      router.replace({
        pathname: `/${routes.payment.root}`,
        query: { interval: 'monthly', plan: 'pro', trial: 'false' },
      });
    }
  }, [isZuoraBasisUser, router]);

  if (isZuoraBasisUser) {
    return <></>;
  }

  // Stripe Basis users are out of scope for SM-392: preserve the pre-existing
  // behavior of showing the new pricing page for a basis user with no licence record.
  if (user?.type === userType.basis && !subscription?.licence?.licenceModelId) {
    return <NewPricingPage />;
  }

  // If user has new pricing plan -> always show new pricing page
  if (currentPlanUsesNewPricing) {
    return <NewPricingPage />;
  }

  // If user has old pricing plan -> show old pricing page (regardless of feature flag)
  return <PricingPage />;
};

export default PricingPageWrapper;
